import { useMutation } from "@apollo/client/react";
import { yupResolver } from "@hookform/resolvers/yup";
import FormInputText from "@repo/components/src/forms/text";
import ModalProvider from "@repo/components/src/globalProviders/modal";
import LoadingButton from "@repo/components/src/shared/loadingButton";
import BasicModal from "@repo/components/src/shared/modal/BasicModal";
import { Box } from "@repo/components/src/ui/box";
import { Button, ButtonText } from "@repo/components/src/ui/button";
import { Icon } from "@repo/components/src/ui/icon";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { RequireMagicLinkDocument } from "@repo/graphql/documents/user/mutation.generated";
import { CheckCircle, Mail } from "lucide-react-native";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Animated, Easing } from "react-native";
import * as yup from "yup";

const emailSchema = yup.object({
  email: yup
    .string()
    .email("Ingresa un correo electrónico válido")
    .required("El correo electrónico es requerido"),
});

type EmailFormValues = yup.InferType<typeof emailSchema>;

const PulsingIcon = () => {
  const scale = useRef(new Animated.Value(1)).current;
  const opacity = useRef(new Animated.Value(0.6)).current;

  useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.parallel([
          Animated.timing(scale, {
            toValue: 1.15,
            duration: 800,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
          Animated.timing(opacity, {
            toValue: 1,
            duration: 800,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
        ]),
        Animated.parallel([
          Animated.timing(scale, {
            toValue: 1,
            duration: 800,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
          Animated.timing(opacity, {
            toValue: 0.6,
            duration: 800,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
        ]),
      ]),
    ).start();
  }, [scale, opacity]);

  return (
    <Animated.View
      style={{ transform: [{ scale }], opacity }}
      className="items-center justify-center"
    >
      <Box className="w-16 h-16 rounded-full bg-brand-100 items-center justify-center">
        <Icon as={Mail} size="xl" className="text-brand-600" />
      </Box>
    </Animated.View>
  );
};

export default function ForgotPasswordModal() {
  const [sent, setSent] = useState(false);

  const [requireMagicLink, { loading }] = useMutation(
    RequireMagicLinkDocument,
    {
      onCompleted: () => {
        setSent(true);
      },
    },
  );

  const formProps = useForm<EmailFormValues>({
    resolver: yupResolver(emailSchema),
    mode: "onSubmit",
    defaultValues: { email: "" },
  });

  const handleSubmit = useCallback(
    (data: EmailFormValues) => {
      requireMagicLink({ variables: { email: data.email } });
    },
    [requireMagicLink],
  );

  if (sent) {
    return (
      <BasicModal
        title="Revisa tu correo"
        showClose={true}
        footer={
          <Button
            action="brand"
            variant="solid"
            className="flex-1"
            rounded="lg"
            onPress={ModalProvider.hide}
          >
            <ButtonText>Entendido</ButtonText>
          </Button>
        }
      >
        <VStack space="lg" className="items-center py-4">
          <Box className="w-16 h-16 rounded-full bg-success-100 items-center justify-center">
            <Icon as={CheckCircle} size="xl" className="text-success-600" />
          </Box>
          <VStack space="xs" className="items-center">
            <Text className="text-center text-typography-700" size="md">
              Si tu correo está registrado, te enviamos un enlace para iniciar
              sesión. Revisa tu bandeja de entrada y la carpeta de spam.
            </Text>
          </VStack>
        </VStack>
      </BasicModal>
    );
  }

  return (
    <BasicModal
      title="Recuperar acceso"
      showClose={true}
      cancelButton={{
        text: "Cancelar",
        props: { className: "flex-1", rounded: "lg" },
      }}
      footer={
        <LoadingButton
          action="brand"
          variant="solid"
          loading={loading}
          loadingText="Enviando..."
          className="flex-1"
          rounded="lg"
          onPress={() => formProps.handleSubmit(handleSubmit)()}
        >
          Enviar enlace
        </LoadingButton>
      }
    >
      <VStack space="lg" className="py-2">
        <VStack space="md" className="items-center">
          <PulsingIcon />
          <Text className="text-center text-typography-600" size="lg">
            Ingresa tu correo electrónico y te enviaremos un enlace para iniciar
            sesión sin contraseña.
          </Text>
        </VStack>
        <FormInputText
          name="email"
          label="Correo electrónico"
          control={formProps.control}
          placeholder="tu@correo.com"
          required
          disabled={loading}
          className=""
          size="md"
          keyboardType="email-address"
          textContentType="emailAddress"
          autoCapitalize="none"
        />
      </VStack>
    </BasicModal>
  );
}
